/**
 * A BATTLE START REFUSED WHILE MOONFALL UPDATES WAITS, AND STARTS BY ITSELF.
 *
 * A release drains the server before it switches to the new version
 * (docs/release-process.md, "Releases that never end a battle"): the battles
 * in progress play on, and a new start is refused with `server_updating`. The
 * owner approved it on 28 October 2026, and the client's part is his: it says
 * "Moonfall is updating. Your next battle can start in a minute." and starts
 * the battle by itself once the new version answers.
 *
 * The press has already painted the match loading screen. It stays; the
 * message and a Cancel button appear on its notice line (MatchLoading
 * holdForUpdate), and the same start is asked again — at once when the
 * connection comes back, which is the new version answering, and otherwise on
 * the server's retry hint, because an aborted release starts battles again on
 * the version already running. `server_restarting` (the switch itself) is the
 * same wait, and so is a start that cannot reach anything while the new
 * version boots, for a few minutes.
 *
 * Only a start under a loading screen waits. Without one (a friend room, which
 * shows none) the refusal goes back to the caller as it came, and the lobby
 * says the same words in its refusal toast.
 */

import { i18n } from '../i18n.js';
import { MatchLoading } from './match-loading.js';

const ServerUpdateWait = (function () {
    'use strict';

    const REPLACEMENT_CODES = new Set(['server_updating', 'server_restarting']);
    const DEFAULT_RETRY_MS = 15000;
    const MIN_RETRY_MS = 2000;
    const MAX_RETRY_MS = 60000;
    /* A start that reaches nothing while the server is replaced: the new
       version boots in well under this. Past it, the lobby's own "Failed to
       connect" question (ui/lobby.js) takes over. */
    const UNREACHED_MAX_MS = 5 * 60 * 1000;

    function uiText(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    function message() {
        return uiText(
            'connection.serverUpdating',
            'Moonfall is updating. Your next battle can start in a minute.',
        );
    }

    function isServerReplacement(result) {
        return result?.ok === false && REPLACEMENT_CODES.has(result.code);
    }

    function retryDelayMs(result) {
        const hint = Number(result?.retryAfterMs);
        if (!Number.isFinite(hint)) return DEFAULT_RETRY_MS;
        return Math.min(MAX_RETRY_MS, Math.max(MIN_RETRY_MS, hint));
    }

    /* The next try: when the hint has passed, or at once when the connection
       comes back. 'cancelled' when the player pressed Cancel or the screen
       was taken down. */
    function nextTry(hold, delayMs, onReconnect) {
        if (hold.isCancelled()) return Promise.resolve('cancelled');
        return new Promise((resolve) => {
            let unsubscribe = null;
            let timer = null;
            const settle = (outcome) => {
                clearTimeout(timer);
                if (typeof unsubscribe === 'function') unsubscribe();
                unsubscribe = null;
                resolve(outcome);
            };
            timer = setTimeout(() => settle('retry'), delayMs);
            try {
                unsubscribe = typeof onReconnect === 'function' ? onReconnect(() => settle('retry')) : null;
            } catch (_) { /* the timer still asks again */ }
            hold.cancelled.then(() => settle('cancelled'));
        });
    }

    /**
     * Run `startOnce` until the server takes the start or refuses it for
     * another reason. Resolves to the last answer, or to
     * `{ ok: false, code: 'start_cancelled' }` when the player cancelled.
     *
     * @param {() => Promise<any>} startOnce one start, exactly as the lobby sends it
     * @param {{ onReconnect?: (listener: () => void) => (() => void) }} [hooks]
     */
    async function run(startOnce, { onReconnect } = {}) {
        let result = await startOnce();
        if (!isServerReplacement(result)) return result;
        const hold = MatchLoading?.holdForUpdate?.({
            message: message(),
            cancelLabel: uiText('common.cancel', 'Cancel'),
        });
        if (!hold) return result;
        let unreachedSince = null;
        try {
            for (;;) {
                // eslint-disable-next-line no-await-in-loop
                const outcome = await nextTry(hold, retryDelayMs(result), onReconnect);
                if (outcome !== 'retry') return { ok: false, code: 'start_cancelled' };
                // A start sent before Cancel may already be building the
                // battle on the server: its answer is the answer.
                // eslint-disable-next-line no-await-in-loop
                const next = await startOnce();
                if (isServerReplacement(next)) {
                    unreachedSince = null;
                    result = next;
                    continue;
                }
                if (next?.unreached === true) {
                    unreachedSince = unreachedSince ?? Date.now();
                    if (Date.now() - unreachedSince < UNREACHED_MAX_MS) continue;
                }
                return next;
            }
        } finally {
            hold.release();
        }
    }

    return {
        UNREACHED_MAX_MS,
        isServerReplacement,
        message,
        run,
    };
})();

export { ServerUpdateWait };
